class PrivatePictureError extends Error {}

const MAX_BYTES = 10 * 1024 * 1024;
export const PRIVATE_RASTER_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp']);
// Native pickers may map MIME types through an incomplete OS type registry.
// Extensions supplement that hint; inspectRaster still owns strict validation.
export const PRIVATE_PICTURE_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

/** Inspect raster headers before decoding; unsupported or oversized rasters fail closed. */
async function inspectRaster(file) {
  if (!file || !PRIVATE_RASTER_TYPES.includes(file.type) || file.size > MAX_BYTES || file.size < 16)
    throw new PrivatePictureError('Choose a JPEG, PNG or WebP photo smaller than 10 MB.');
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let width = 0,
    height = 0;
  if (
    file.type === 'image/png' &&
    bytes.length >= 24 &&
    view.getUint32(0) === 0x89504e47 &&
    view.getUint32(4) === 0x0d0a1a0a
  ) {
    width = view.getUint32(16);
    height = view.getUint32(20);
  } else if (file.type === 'image/jpeg' && view.getUint16(0) === 0xffd8) {
    for (let at = 2; at + 9 < bytes.length;) {
      if (bytes[at] !== 255) break;
      const marker = bytes[at + 1];
      if (marker === 0xd9 || marker === 0xda) break;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        at += 2;
        continue;
      }
      const size = view.getUint16(at + 2);
      if (size < 2 || at + 2 + size > bytes.length) break;
      if ([0xc0, 0xc1, 0xc2].includes(marker)) {
        height = view.getUint16(at + 5);
        width = view.getUint16(at + 7);
        break;
      }
      at += size + 2;
    }
  } else if (
    file.type === 'image/webp' &&
    view.getUint32(0) === 0x52494646 &&
    view.getUint32(8) === 0x57454250
  ) {
    const kind = view.getUint32(12);
    if (kind === 0x56503858 && bytes.length >= 30 && !(bytes[20] & 2)) {
      width = 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16);
      height = 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16);
    } else if (kind === 0x5650384c && bytes[20] === 0x2f && bytes.length >= 25) {
      const packed = view.getUint32(21, true);
      width = (packed & 0x3fff) + 1;
      height = ((packed >>> 14) & 0x3fff) + 1;
    } else if (
      kind === 0x56503820 &&
      bytes.length >= 30 &&
      bytes[23] === 0x9d &&
      bytes[24] === 1 &&
      bytes[25] === 0x2a
    ) {
      width = view.getUint16(26, true) & 0x3fff;
      height = view.getUint16(28, true) & 0x3fff;
    }
  }
  if (!width || !height || width > 4096 || height > 4096 || width * height > 16_000_000)
    throw new PrivatePictureError(
      'Choose a still photo up to 4096 pixels per side and 16 megapixels.',
    );
  return { width, height };
}

/** Existing Photo Sands projection: fit the validated raster inside128x192. */
export async function photoDimensions(file) {
  const { width, height } = await inspectRaster(file);
  const scale = Math.min(1, 128 / width, 192 / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Decode a local still photo into a bounded metadata-free temporary texture.
 * The caller owns the successful handle and must dispose it on replacement or
 * card disposal. isCurrent fences asynchronous work; stale results never publish
 * a URL. No upload, network, persistent storage or original filename is used. */
export async function loadPrivatePicture(file, { isCurrent = () => true } = {}) {
  let header;
  try {
    header = await inspectRaster(file);
  } catch (error) {
    throw error instanceof PrivatePictureError
      ? error
      : new PrivatePictureError('Unable to read this private picture.');
  }
  const current = () => {
    if (!isCurrent()) throw new PrivatePictureError('This photo selection is no longer current.');
  };
  current();
  if (typeof globalThis.createImageBitmap !== 'function' || !globalThis.document?.createElement)
    throw new PrivatePictureError('Private pictures need local raster decoding support.');
  let bitmap, url;
  try {
    // Source allocation is bounded by inspected4096/16MP dimensions. Decode
    // orientation first: choosing resize dimensions from an unrotated JPEG
    // header would stretch an EXIF-rotated portrait before canvas reencoding.
    bitmap = await globalThis.createImageBitmap(file, { imageOrientation: 'from-image' });
    current();
    const { width: w, height: h } = bitmap;
    if (
      !Number.isInteger(w) ||
      !Number.isInteger(h) ||
      w < 1 ||
      h < 1 ||
      w > 4096 ||
      h > 4096 ||
      w * h > 16_000_000 ||
      !(
        (w === header.width && h === header.height) ||
        (file.type === 'image/jpeg' && w === header.height && h === header.width)
      )
    )
      throw new PrivatePictureError('This photo did not decode to its validated dimensions.');
    const scale = Math.min(1, 1536 / Math.max(w, h));
    const width = Math.max(1, Math.round(w * scale)),
      height = Math.max(1, Math.round(h * scale));
    if (width * height > 2_359_296)
      throw new PrivatePictureError('Private picture exceeds its texture bounds.');
    const canvas = globalThis.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const drawing = canvas.getContext('2d');
    if (!drawing || typeof canvas.toBlob !== 'function')
      throw new PrivatePictureError('Private picture canvas is unavailable.');
    drawing.imageSmoothingEnabled = true;
    drawing.imageSmoothingQuality = 'high';
    drawing.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((value) => {
        if (value?.type === 'image/png' && value.size > 0) resolve(value);
        else reject(new PrivatePictureError('Unable to prepare this private picture.'));
      }, 'image/png'),
    );
    current();
    url = globalThis.URL.createObjectURL(blob);
    current();
    let disposed = false;
    return {
      url,
      width,
      height,
      dispose() {
        if (disposed) return;
        disposed = true;
        globalThis.URL.revokeObjectURL(url);
      },
    };
  } catch (error) {
    if (url) globalThis.URL.revokeObjectURL(url);
    throw error instanceof PrivatePictureError
      ? error
      : new PrivatePictureError('Unable to prepare this private picture.');
  } finally {
    bitmap?.close();
  }
}
